import { useMemo } from 'react';

import { Country } from '@dreryk/address-common-api';
import { IdentityDocument } from '@dreryk/egabinet-patient-published-language-api';

import {
   Bean,
   BindingCreator,
   commonTranslationKeys,
   createTranslatedText,
   getCurrentLocale,
   ValidationRules,
} from '@dreryk/react-common';
import { Select, useSelectContext } from '@dreryk/react-components';

import { identityDocumentTranslationKeys } from '../../translations';
import { countryIsoCodeGetter, createCountryFilter } from '../common.utils';

const getCountryLabel = (item?: Country): string => item?.getName(getCurrentLocale()) ?? '';

type IdentityDocumentIssuingCountryFieldProps = {
   readonly name: string;
   readonly register: BindingCreator<Bean>;
   readonly testId?: string;
};

const countries = Country.searchAll(getCurrentLocale());
const CountriesList = (): JSX.Element => {
   const { searchText } = useSelectContext();
   const filteredCountries = countries.filter(createCountryFilter(searchText));
   if (!filteredCountries.length) {
      return <Select.NoItems testId={'noCountries'} />;
   }
   return (
      <Select.List<Country> items={filteredCountries} keyExtractor={countryIsoCodeGetter}>
         {(item: Country) => (
            <Select.Item<Country>
               item={item}
               testId={item.getIsoCode()}
               label={<Select.Highlight node={createTranslatedText(getCountryLabel(item))} />}
            />
         )}
      </Select.List>
   );
};

export const IdentityDocumentIssuingCountryField = (props: IdentityDocumentIssuingCountryFieldProps): JSX.Element => {
   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      return {
         required: true,
         validate: (country) => {
            return country instanceof Country && IdentityDocument.isIssuingCountryValid(country)
               ? true
               : identityDocumentTranslationKeys.identityDocumentIssuingCountry.validate.invalidIssuingCountry;
         },
      };
   }, []);

   return (
      <Select<Country>
         testId={props.testId ?? 'identityDocumentIssuingCountry'}
         triggerLabelExtractor={getCountryLabel}
         placeholder={commonTranslationKeys.choose}
         controller={props.register(props.name, validationRules)}
         label={identityDocumentTranslationKeys.identityDocumentIssuingCountry.label}
         defaultValue={null}
         maxHeight={190}
         searchInputEnabled
      >
         <CountriesList />
      </Select>
   );
};
